import type { ProductCardData } from '@/components/brand';
import type { StoreHit } from '@/lib/algolia/stores';
import type { FilterState, ProductHit } from '@/lib/algolia/types';
import type { SearchTab } from '@/lib/paths';
import { getCumulativeSearchHitsPerPage } from '@/lib/search/constants';
import { transformHitsToProductCards } from '@/lib/search/transformHits';

export interface ClientTabLoadResult {
  products: ProductCardData[];
  stores?: StoreHit[];
  page: number;
  totalHits: number;
}

const TAB_TO_PRODUCT_TYPE: Record<'watches' | 'straps' | 'eyewear', 'Watch' | 'Strap' | 'Eyewear'> =
  {
    watches: 'Watch',
    straps: 'Strap',
    eyewear: 'Eyewear',
  };

function getCountryCodeFromLocale(locale: string): string {
  return locale.split('-')[0].toUpperCase();
}

async function fetchProductLoadMore(
  locale: string,
  tab: 'watches' | 'straps' | 'eyewear',
  query: string,
  page: number,
  filters: Partial<FilterState>,
  badgeTranslations: (key: string) => string
): Promise<ClientTabLoadResult> {
  const cumulativeHits = getCumulativeSearchHitsPerPage(page);
  const response = await fetch('/api/products/search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      locale,
      productType: TAB_TO_PRODUCT_TYPE[tab],
      filterContext: 'search',
      filters: { ...filters, query, page: 1 },
      hitsPerPage: cumulativeHits,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to load more search results');
  }

  const json = (await response.json()) as {
    success: boolean;
    data: { hits: ProductHit[]; totalHits: number };
  };

  const products = transformHitsToProductCards(json.data.hits, locale, badgeTranslations);

  return {
    products,
    page,
    totalHits: json.data.totalHits,
  };
}

async function fetchStoresLoadMore(
  locale: string,
  query: string,
  page: number,
  storeTypeFilter?: string[]
): Promise<ClientTabLoadResult> {
  const cumulativeHits = getCumulativeSearchHitsPerPage(page);
  const params = new URLSearchParams({
    country: getCountryCodeFromLocale(locale),
    mode: 'Search',
    limit: String(cumulativeHits),
    page: '0',
  });

  if (query) {
    params.set('q', query);
  }

  if (storeTypeFilter?.length) {
    params.set('storeType', storeTypeFilter.join(','));
  }

  const response = await fetch(`/api/stores?${params.toString()}`);

  if (!response.ok) {
    throw new Error('Failed to load more store results');
  }

  const json = (await response.json()) as {
    success: boolean;
    data: { stores: StoreHit[]; totalHits: number };
  };

  return {
    products: [],
    stores: json.data.stores,
    page,
    totalHits: json.data.totalHits,
  };
}

/**
 * Client-side cumulative load-more for a search tab (Breitling parity — no full navigation).
 */
export async function fetchSearchTabLoadMore(options: {
  locale: string;
  tab: SearchTab;
  query: string;
  page: number;
  filters?: Partial<FilterState>;
  storeTypeFilter?: string[];
  badgeTranslations: (key: string) => string;
}): Promise<ClientTabLoadResult> {
  const { locale, tab, query, page, filters = {}, storeTypeFilter, badgeTranslations } = options;

  if (tab === 'stores') {
    return fetchStoresLoadMore(locale, query, page, storeTypeFilter);
  }

  if (tab === 'watches' || tab === 'straps' || tab === 'eyewear') {
    return fetchProductLoadMore(locale, tab, query, page, filters, badgeTranslations);
  }

  throw new Error(`Cannot load more for tab: ${tab}`);
}

/**
 * Native History.replaceState — Next patches `history.replaceState` and remounts
 * on path-segment changes. Passing `null` as state also wipes App Router metadata.
 */
export function replaceUrlWithoutNavigation(nextUrl: string): void {
  History.prototype.replaceState.call(globalThis.history, globalThis.history.state, '', nextUrl);
}

/** Sync ?page= in the URL without triggering a Next.js navigation. */
export function syncSearchPageUrl(
  lang: string,
  tab: SearchTab,
  query: string,
  page: number,
  preserveExistingParams: boolean
): void {
  const params = preserveExistingParams
    ? new URLSearchParams(globalThis.location.search)
    : new URLSearchParams();

  if (query) {
    params.set('q', query);
  } else {
    params.delete('q');
  }

  if (page > 1) {
    params.set('page', String(page));
  } else {
    params.delete('page');
  }

  const queryString = params.toString();
  const path = `/${lang}/search/${tab}`;
  const nextUrl = queryString ? `${path}?${queryString}` : path;
  replaceUrlWithoutNavigation(nextUrl);
}
